import React, { useCallback, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import type { IconProp } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import DropdownMenu, { DropdownItem } from '@atlaskit/dropdown-menu';
import type { CustomTriggerProps } from '@atlaskit/dropdown-menu';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import Lozenge from '@atlaskit/lozenge/new';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

export type SuggestionItem = {
	iconUrl: string;
	summary: string;
	/**
	 * The rendered timestamp content. Accepts a plain string or any ReactNode
	 * so callers can pass a live `<StaticTimeAgo />` element directly.
	 */
	timestamp: ReactNode;
};

export type ActivityItem = SuggestionItem & {
	status: ActivityStatus;
};

type ActivityStatus = 'success' | 'failed';

type SuggestionCardProps =
	| {
			item: SuggestionItem;
			activity?: false;
			status?: never;
			onReview: () => void;
	  }
	| {
			item: ActivityItem;
			activity: true;
			status: ActivityStatus;
			onReview?: never;
	  };

export function SuggestionCard(props: SuggestionCardProps) {
	const { item } = props;
	const { formatMessage } = useIntl();

	const renderMoreOptionsTrigger = useCallback(
		({ triggerRef, ...triggerProps }: CustomTriggerProps<HTMLButtonElement>) => (
			<IconButton
				{...triggerProps}
				ref={triggerRef}
				icon={ShowMoreHorizontalIcon as IconProp}
				label={formatMessage(i18n.moreOptions)}
				appearance="subtle"
			/>
		),
		[formatMessage],
	);

	return (
		<div css={styles.card}>
			<Inline alignBlock="center" space="space.150" spread="space-between">
				<Inline alignBlock="start" space="space.150" grow="hug">
					<Box
						xcss={styles.iconWrapper}
						style={{ width: `${ICON_WRAPPER_SIZE}px`, height: `${ICON_WRAPPER_SIZE}px` }}
					>
						<img src={item.iconUrl} alt="" width={ICON_SIZE} height={ICON_SIZE} />
					</Box>
					<Stack space="space.050">
						<Text>{item.summary}</Text>
						<Text size="small" color="color.text.subtlest">
							{item.timestamp}
						</Text>
					</Stack>
				</Inline>
				{props.activity ? (
					<ActivityStatusLozenge status={props.status} />
				) : (
					<Inline space="space.075" alignBlock="center">
						<Button onClick={props.onReview}>{formatMessage(i18n.reviewButton)}</Button>
						<DropdownMenu trigger={renderMoreOptionsTrigger}>
							<DropdownItem>{formatMessage(i18n.placeholderItem)}</DropdownItem>
						</DropdownMenu>
					</Inline>
				)}
			</Inline>
		</div>
	);
}

function ActivityStatusLozenge({ status }: { status: ActivityStatus }) {
	const { formatMessage } = useIntl();

	if (status === 'success') {
		return (
			<Lozenge appearance="success" iconBefore={StatusSuccessIcon}>
				{formatMessage(i18n.statusSuccess)}
			</Lozenge>
		);
	}

	return (
		<Lozenge appearance="warning" iconBefore={StatusWarningIcon}>
			{formatMessage(i18n.statusFailed)}
		</Lozenge>
	);
}

const ICON_SIZE = 16;
const ICON_WRAPPER_SIZE = 38;

const styles = cssMap({
	card: {
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	iconWrapper: {
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		backgroundColor: token('color.background.neutral'),
	},
});

const i18n = defineMessages({
	reviewButton: {
		id: 'space-suggestions.suggestion-card.review-button',
		defaultMessage: 'Review',
		description:
			'Label for the button that opens the right-rail review panel for a suggestion that needs review',
	},
	moreOptions: {
		id: 'space-suggestions.suggestion-card.more-options',
		defaultMessage: 'More options',
		description:
			'Accessible label for the icon button that opens additional options for a suggestion card',
	},
	placeholderItem: {
		id: 'space-suggestions.suggestion-card.placeholder-item',
		defaultMessage: 'Placeholder item',
		description:
			'Placeholder dropdown menu item for a suggestion card — to be replaced with real actions',
	},
	statusSuccess: {
		id: 'space-suggestions.suggestion-card.status-success',
		defaultMessage: 'Success',
		description:
			'Lozenge label shown on an activity card when the agent action completed successfully',
	},
	statusFailed: {
		id: 'space-suggestions.suggestion-card.status-failed',
		defaultMessage: 'Failed',
		description: 'Lozenge label shown on an activity card when the agent action failed',
	},
});
